/**
 * Geofence + terms-of-use logic (#769), pure and platform-neutral. The Android and iOS ports must match this file
 * case for case: shared/fixtures/geo.json (generated from this code by `npm run fixtures`) pins every behaviour.
 *
 * Inputs:
 *   - shared/policy/jurisdictions.json: country (or "CC-REGION") -> "blocked" | "browse_only" | "allowed".
 *     Mirrors Hyperliquid's own frontend (docs/research/GEOFENCE_HYPERLIQUID.md); NOT legal advice, TODO-legal.
 *   - shared/policy/terms.json: the versioned terms text.
 *   - /geo.json: `{ "country": "US", "region": "NY" }`, answered by the CloudFront Function in
 *     deploy/cloudfront/geo-viewer-request.js from the CloudFront-Viewer-Country(-Region) headers.
 *
 * Unknown country (no /geo.json, a malformed answer, a timeout, a code CloudFront uses for "unknown"): the policy's
 * `unknown` mode applies, which is "browse_only" (like Hyperliquid: browsing works, trading stays off until the
 * location is known). The caller logs it and retries. Rationale: shared/policy/README.md.
 */

export type GeoMode = 'blocked' | 'browse_only' | 'allowed';

export const GEO_MODES: readonly GeoMode[] = ['blocked', 'browse_only', 'allowed'];

export interface JurisdictionRule {
  mode: GeoMode;
  note?: string;
}

export interface JurisdictionPolicy {
  schema: string;
  version: string;
  /** Mode for a known country with no rule. */
  default: GeoMode;
  /** Mode when the country could not be determined. */
  unknown: GeoMode;
  /** Keys: ISO 3166-1 alpha-2 country ("US"), or country + "-" + CloudFront region code ("CA-ON", "UA-43"). */
  jurisdictions: Record<string, JurisdictionRule>;
}

/** Where the viewer is, as far as we know. Both fields normalised (upper case) or null. */
export interface GeoLocation {
  country: string | null;
  region: string | null;
}

export interface GeoDecision {
  mode: GeoMode;
  /** The normalised country, or null when unknown. */
  country: string | null;
  region: string | null;
  /** Which policy entry decided: a jurisdictions key ("CA-ON", "US"), "default" or "unknown". */
  matched: string;
}

export function isGeoMode(v: unknown): v is GeoMode {
  return v === 'blocked' || v === 'browse_only' || v === 'allowed';
}

/**
 * Codes that mean "no real country": CloudFront and GeoIP databases use XX / ZZ, and some use A1 (anonymous
 * proxy), A2 (satellite), O1 (other), T1 (Tor), EU / AP (continent only).
 */
const NOT_A_COUNTRY = new Set(['XX', 'ZZ', 'A1', 'A2', 'O1', 'T1', 'EU', 'AP']);

/** "us " -> "US"; anything that is not two letters (or is a placeholder code) -> null. */
export function normalizeCountry(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const c = v.trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(c) || NOT_A_COUNTRY.has(c)) return null;
  return c;
}

/** CloudFront-Viewer-Country-Region is the ISO 3166-2 subdivision without the country ("ON", "43", "NY"). */
export function normalizeRegion(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  let r = v.trim().toUpperCase();
  // Tolerate a full ISO 3166-2 code ("CA-ON").
  const dash = r.indexOf('-');
  if (dash === 2) r = r.slice(3);
  if (!/^[A-Z0-9]{1,3}$/.test(r)) return null;
  return r;
}

/**
 * Validate a policy file. Throws on anything that would make the decision ambiguous (bad mode, bad key), so a typo
 * in jurisdictions.json fails the unit tests instead of silently allowing a country.
 */
export function parsePolicy(raw: unknown): JurisdictionPolicy {
  if (!raw || typeof raw !== 'object') throw new Error('policy: not an object');
  const p = raw as Record<string, unknown>;
  if (typeof p.schema !== 'string' || !p.schema.startsWith('hip4.policy.jurisdictions/')) throw new Error('policy: schema');
  if (typeof p.version !== 'string' || !p.version) throw new Error('policy: version');
  if (!isGeoMode(p.default)) throw new Error('policy: default');
  if (!isGeoMode(p.unknown)) throw new Error('policy: unknown');
  if (!p.jurisdictions || typeof p.jurisdictions !== 'object') throw new Error('policy: jurisdictions');
  const jurisdictions: Record<string, JurisdictionRule> = {};
  for (const [key, rule] of Object.entries(p.jurisdictions as Record<string, unknown>)) {
    if (!/^[A-Z]{2}(-[A-Z0-9]{1,3})?$/.test(key)) throw new Error(`policy: bad key ${key}`);
    const r = rule as Record<string, unknown> | null;
    if (!r || !isGeoMode(r.mode)) throw new Error(`policy: bad mode for ${key}`);
    jurisdictions[key] = typeof r.note === 'string' ? { mode: r.mode, note: r.note } : { mode: r.mode };
  }
  return { schema: p.schema, version: p.version, default: p.default, unknown: p.unknown, jurisdictions };
}

/**
 * The mode for a location. A region rule ("CA-ON") wins over its country rule ("CA"); a country with no rule gets
 * `default`; an unknown country gets `unknown` (the region is ignored without a country).
 */
export function resolveMode(policy: JurisdictionPolicy, loc: { country?: unknown; region?: unknown }): GeoDecision {
  const country = normalizeCountry(loc.country);
  if (!country) return { mode: policy.unknown, country: null, region: null, matched: 'unknown' };
  const region = normalizeRegion(loc.region);
  if (region) {
    const key = `${country}-${region}`;
    const r = policy.jurisdictions[key];
    if (r) return { mode: r.mode, country, region, matched: key };
  }
  const c = policy.jurisdictions[country];
  if (c) return { mode: c.mode, country, region, matched: country };
  return { mode: policy.default, country, region, matched: 'default' };
}

/**
 * Parse the body of /geo.json. Returns null when it is not the JSON we expect (for example the SPA's index.html,
 * which CloudFront serves for unknown paths until the function is attached).
 */
export function parseGeoResponse(body: string): GeoLocation | null {
  let j: unknown;
  try {
    j = JSON.parse(body);
  } catch {
    return null;
  }
  if (!j || typeof j !== 'object' || Array.isArray(j)) return null;
  const o = j as Record<string, unknown>;
  if (!('country' in o)) return null;
  return { country: normalizeCountry(o.country), region: normalizeRegion(o.region) };
}

/** Trading (order ticket, 1-click trading, approvals, Close) is allowed only in "allowed". */
export function canTrade(mode: GeoMode): boolean {
  return mode === 'allowed';
}

/** The Hyperliquid network the app is on. Anything else is treated as mainnet (fail closed). */
export type GeoNetwork = 'mainnet' | 'testnet';

/**
 * The mode that applies on a network (owner decision D1, 2026-10-19). Like Hyperliquid's own frontend
 * (`performIpRestrictions = chain === "Mainnet"`), browse-only applies on MAINNET only: on testnet everyone may trade,
 * including viewers whose location is unknown. "blocked" (the UK: outcome markets not offered) applies on both
 * networks: the blocked page is network-independent. Input: the jurisdiction mode from resolveMode().
 */
export function effectiveMode(mode: GeoMode, network: GeoNetwork | string): GeoMode {
  if (mode === 'browse_only' && network === 'testnet') return 'allowed';
  return mode;
}

/**
 * May the app register this device / browser for push notifications (owner decision D6)? Yes for allowed and
 * browse-only viewers (push is read-only, like the portfolio), never for blocked ones. Network-independent. Input: the
 * jurisdiction mode (resolveMode().mode), not effectiveMode(), although the two agree here.
 */
export function pushAllowed(mode: GeoMode): boolean {
  return mode !== 'blocked';
}

/**
 * The stricter of two modes (blocked > browse_only > allowed). Hook for owner decision D7 (#769 part 2, a separate MR):
 * Hyperliquid's `legalCheck` becomes a second opinion mapped to a GeoMode and combined with our edge decision here,
 * before networkAccess(). Not used by the app yet.
 */
export function stricterMode(a: GeoMode, b: GeoMode): GeoMode {
  const rank: Record<GeoMode, number> = { allowed: 0, browse_only: 1, blocked: 2 };
  return rank[a] >= rank[b] ? a : b;
}

/** What a jurisdiction decision allows on a network: the one function every platform's UI and guards read. */
export interface NetworkAccess {
  /** effectiveMode(decision.mode, network). */
  mode: GeoMode;
  /** Ticket submit, 1-click trading (approveAgent), builder-fee approval, Close. Cancels are always allowed. */
  canTrade: boolean;
  /** Push registration (D6). */
  pushAllowed: boolean;
}

export function networkAccess(decision: Pick<GeoDecision, 'mode'>, network: GeoNetwork | string): NetworkAccess {
  const mode = effectiveMode(decision.mode, network);
  return { mode, canTrade: canTrade(mode), pushAllowed: pushAllowed(decision.mode) };
}

// ---------------------------------------------------------------------------------------------------------------
// Terms of use

export interface TermsSection {
  heading: string;
  paragraphs: string[];
}

export interface TermsDoc {
  schema: string;
  version: string;
  effectiveDate: string;
  title: string;
  /** Not final: the dialog shows `draftNotice` ("DRAFT — requires legal review") above the text. */
  draft: boolean;
  draftNotice?: string;
  /** Preamble shown above the sections. */
  intro?: string;
  sections: TermsSection[];
  acceptLabel: string;
  attestation: string;
}

/** What is stored after the user accepts (localStorage `outfox.terms` on web). */
export interface TermsAcceptance {
  version: string;
  /** ISO-8601 time of acceptance. */
  acceptedAt: string;
}

export function parseTerms(raw: unknown): TermsDoc {
  if (!raw || typeof raw !== 'object') throw new Error('terms: not an object');
  const t = raw as Record<string, unknown>;
  if (typeof t.schema !== 'string' || !t.schema.startsWith('hip4.policy.terms/')) throw new Error('terms: schema');
  if (typeof t.version !== 'string' || !t.version.trim()) throw new Error('terms: version');
  if (typeof t.draft !== 'boolean') throw new Error('terms: draft');
  if (t.draft && (typeof t.draftNotice !== 'string' || !t.draftNotice.trim())) throw new Error('terms: draftNotice');
  if (t.intro !== undefined && typeof t.intro !== 'string') throw new Error('terms: intro');
  if (!Array.isArray(t.sections) || t.sections.length === 0) throw new Error('terms: sections');
  for (const s of t.sections as unknown[]) {
    const sec = s as Record<string, unknown> | null;
    if (!sec || typeof sec.heading !== 'string' || !Array.isArray(sec.paragraphs)) throw new Error('terms: section');
    if (!(sec.paragraphs as unknown[]).every((x) => typeof x === 'string')) throw new Error('terms: paragraph');
  }
  return raw as TermsDoc;
}

/** Parse the stored acceptance; null when missing or malformed (which means: ask again). */
export function parseAcceptance(raw: string | null | undefined): TermsAcceptance | null {
  if (!raw) return null;
  let j: unknown;
  try {
    j = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!j || typeof j !== 'object') return null;
  const o = j as Record<string, unknown>;
  if (typeof o.version !== 'string' || !o.version) return null;
  if (typeof o.acceptedAt !== 'string') return null;
  return { version: o.version, acceptedAt: o.acceptedAt };
}

/**
 * Must the terms gate be shown? Yes unless the stored acceptance is for exactly the current version. Versions are
 * opaque strings compared for equality: any change (including a rollback) asks again.
 */
export function needsTermsPrompt(storedRaw: string | null | undefined, currentVersion: string): boolean {
  const a = parseAcceptance(storedRaw);
  return !a || a.version !== currentVersion;
}

/** The stored value for an acceptance of `version` at `now`. */
export function acceptanceRecord(version: string, now: Date): string {
  const rec: TermsAcceptance = { version, acceptedAt: now.toISOString() };
  return JSON.stringify(rec);
}
